import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { FiArrowUpRight, FiGlobe, FiInstagram, FiLinkedin, FiMoon, FiSearch, FiSun, FiX } from "react-icons/fi";
import { HiOutlineMenuAlt4 } from 'react-icons/hi';
import { servicesData } from "../Components/serviceMetaData";
import { getPublicSeoRoutes, serviceSlug } from "../seo/publicSeo";
import { hasVoicesInteriorScene, isVoicesRoute } from './voicesRoutes';
import { useVoicesTheme } from './VoicesTheme';
import "./voices-shell.css";

const PAGES = [
  { pathname: "/", title: "Home", category: "Antrazal" },
  { pathname: "/services", title: "All services", category: "Expertise" },
  { pathname: "/work", title: "Our work", category: "Work" },
  { pathname: "/who-we-are", title: "Company", category: "Antrazal" },
  { pathname: "/case-studies", title: "Case studies & white papers", category: "Work" },
  { pathname: "/careers", title: "Careers", category: "Antrazal" },
  { pathname: "/events", title: "Events", category: "Antrazal" },
  { pathname: "/products/edi-converter", title: "EDI converter", category: "Products" },
  { pathname: "/contact", title: "Contact", category: "Antrazal" },
  { pathname: "/site-map", title: "Site directory", category: "Antrazal" },
  { pathname: "/privacy-policy", title: "Privacy policy", category: "Antrazal" },
];
const FEATURED_SERVICES = [
  ["/ai-agents", "AI agent design"],
  ["/services/chat-agents", "Custom AI agents"],
  ["/services/salesforce-agentforce", "Salesforce Agentforce"],
  ["/services/support-and-optimization-services", "Managed services"],
  ["/managed-agents", "Managed agents"],
  ["/implementation-guide", "Implementation guide"],
];
// Match the region/language choices in GoogleTranslate and MobileGoogleTranslate.
const LANGUAGE_NAMES = { en: "English", fr: "Fran\u00e7ais", es: "Espa\u00f1ol", de: "Deutsch", ja: "\u65e5\u672c\u8a9e" };
const LANGUAGE_REGIONS = [
  ["United States", ["en"]],
  ["Australia", ["en"]],
  ["Canada", ["en", "fr"]],
  ["Colombia", ["en", "es"]],
  ["Germany", ["en", "de"]],
  ["Ireland", ["en"]],
  ["Japan", ["en", "ja"]],
  ["Mexico", ["en", "es"]],
  ["New Zealand", ["en"]],
  ["Singapore", ["en"]],
  ["United Kingdom", ["en"]],
];
const LANGUAGE_OPTIONS = LANGUAGE_REGIONS.flatMap(([country, languages]) => languages.map((code) => ({ country, code, value: `${country}:${code}` })));
const FOCUSABLE = 'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

function getFocusable(root) {
  return Array.from(root.querySelectorAll(FOCUSABLE)).filter(
    (element) => element.tabIndex >= 0 && !element.closest('[hidden], [inert], [aria-hidden="true"]')
  );
}

function getDirectory(blogs) {
  const records = new Map(PAGES.map((page) => [page.pathname, page]));
  // Route keys must follow App/Sales, not the optional (sometimes different) data.slug.
  servicesData.filter((service) => service.name).forEach((service) => {
    const pathname = `/services/${serviceSlug(service.name)}`;
    records.set(pathname, {
      pathname,
      title: service.name,
      category: service.category || "Services",
      description: service.shortDescription || service.headline || "",
    });
  });
  getPublicSeoRoutes(blogs).forEach((page) => {
    const existing = records.get(page.pathname);
    let category = "Antrazal";
    if (page.pathname.startsWith("/customer-stories/")) category = "Customer story";
    if (page.pathname.startsWith("/blog/")) category = "Blog";
    if (page.pathname.startsWith("/article/")) category = "Article";
    if (page.pathname.startsWith("/services/")) category = "Services";
    records.set(page.pathname, {
      pathname: page.pathname,
      title: existing?.title || page.title.replace(/\s*\|\s*Antrazal(?: Solutions)?\s*$/i, ""),
      description: existing?.description || page.description,
      category: existing?.category || category,
    });
  });
  return Array.from(records.values());
}

function moveLinkFocus(event) {
  if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || !event.target.closest("a")) return;
  const links = Array.from(event.currentTarget.querySelectorAll("a[href]"));
  const current = links.indexOf(event.target.closest("a"));
  let next;
  if (["ArrowDown", "ArrowRight"].includes(event.key)) next = (current + 1) % links.length;
  if (["ArrowUp", "ArrowLeft"].includes(event.key)) next = (current - 1 + links.length) % links.length;
  if (event.key === "Home") next = 0;
  if (event.key === "End") next = links.length - 1;
  if (next !== undefined) {
    event.preventDefault();
    links[next]?.focus();
  }
}

function ShellDialog({ id, title, kind, onClose, returnFocus, initialFocus, children }) {
  const dialogRef = useRef(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    const opener = returnFocus.current;
    if (typeof dialog.showModal === "function") dialog.showModal();
    else dialog.setAttribute("open", "");

    const focusStart = () => (initialFocus?.current || getFocusable(dialog)[0] || dialog).focus();
    focusStart();
    const containFocus = (event) => {
      if (!dialog.contains(event.target)) focusStart();
    };
    document.addEventListener("focusin", containFocus);
    return () => {
      document.removeEventListener("focusin", containFocus);
      if (dialog.open && typeof dialog.close === "function") dialog.close();
      if (opener?.isConnected && !opener.closest("[inert]")) opener.focus({ preventScroll: true });
    };
  }, [initialFocus, returnFocus]);

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      onClose();
    }
    if (event.key !== "Tab") return;
    const items = getFocusable(dialogRef.current);
    const first = items[0];
    const last = items[items.length - 1];
    if (!first) {
      event.preventDefault();
      dialogRef.current.focus();
    } else if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialogRef.current)) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <dialog
      ref={dialogRef}
      id={id}
      className={`voices-dialog${kind ? ` voices-dialog--${kind}` : ''}`}
      aria-modal="true"
      aria-labelledby={`${id}-title`}
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      onCancel={(event) => { event.preventDefault(); onClose(); }}
      onClick={(event) => {
        if (event.target !== event.currentTarget) return;
        const rect = event.currentTarget.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) onClose();
      }}
    >
      <div className="voices-dialog-bar">
        <h2 id={`${id}-title`} aria-label={title}>{kind === 'menu' ? 'Explore Antrazal' : title}</h2>
        <button type="button" className="voices-icon-button" aria-label={`Close ${title.toLowerCase()}`} title="Close" onClick={onClose}>
          <FiX aria-hidden="true" />
        </button>
      </div>
      <div className="voices-dialog-content">{children}</div>
    </dialog>
  );
}

function SiteSearch({ records, inputRef, onClose }) {
  const [query, setQuery] = useState("");
  const resultsRef = useRef(null);
  const id = useId();
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const results = records.filter((page) => {
    const text = `${page.title} ${page.category} ${page.description || ""} ${page.pathname}`.toLowerCase();
    return words.every((word) => text.includes(word));
  });

  const searchKeyDown = (event) => {
    if (event.nativeEvent.isComposing || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    const links = Array.from(resultsRef.current.querySelectorAll("a"));
    if (!["ArrowDown", "ArrowUp", "Enter"].includes(event.key) || !links.length) return;
    event.preventDefault();
    if (event.key === "Enter") links[0].click();
    else links[event.key === "ArrowDown" ? 0 : links.length - 1].focus();
  };

  return (
    <>
      <div className="voices-search-field">
        <FiSearch aria-hidden="true" />
        <input
          ref={inputRef}
          type="search"
          aria-label="Search public pages"
          aria-controls={`${id}-results`}
          autoComplete="off"
          placeholder="Search Antrazal"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={searchKeyDown}
        />
      </div>
      <p className="voices-search-count" role="status">{results.length} {results.length === 1 ? "page" : "pages"}</p>
      <ul
        ref={resultsRef}
        id={`${id}-results`}
        className="voices-search-results"
        aria-label="Search results"
        onKeyDown={(event) => {
          const links = Array.from(event.currentTarget.querySelectorAll("a"));
          if ((event.key === "ArrowUp" && event.target === links[0]) || (event.key === "ArrowDown" && event.target === links[links.length - 1])) {
            event.preventDefault();
            inputRef.current.focus();
          } else moveLinkFocus(event);
        }}
      >
        {results.map((page) => (
          <li key={page.pathname}>
            <Link to={page.pathname} onClick={onClose}>
              <span><small>{page.category}</small><span>{page.title}</span></span>
              <FiArrowUpRight aria-hidden="true" />
            </Link>
          </li>
        ))}
      </ul>
      {!results.length && <p className="voices-search-empty">No matching pages.</p>}
    </>
  );
}

function LanguageLink({ pathname }) {
  const [language, setLanguage] = useState(LANGUAGE_OPTIONS[0]);
  const id = useId();
  // An external translator cannot leave scripts, cookies or translated DOM on private routes.
  const params = new URLSearchParams({ sl: "en", tl: language.code, u: `https://www.antrazal.com${pathname}` });
  return (
    <div className="voices-language">
      <label htmlFor={id}><FiGlobe aria-hidden="true" />Language</label>
      <select id={id} value={language.value} onChange={(event) => setLanguage(LANGUAGE_OPTIONS.find((option) => option.value === event.target.value))}>
        {LANGUAGE_REGIONS.map(([country, languages]) => (
          <optgroup key={country} label={country}>
            {languages.map((code) => <option key={code} value={`${country}:${code}`}>{LANGUAGE_NAMES[code]}</option>)}
          </optgroup>
        ))}
      </select>
      <a href={`https://translate.google.com/translate?${params}`} target="_blank" rel="noopener noreferrer" aria-label="Google Translate (opens in a new tab)">
        Google Translate<FiArrowUpRight aria-hidden="true" />
      </a>
    </div>
  );
}

export default function VoicesHeader({ blogs = [] }) {
  const location = useLocation();
  const { theme, toggleTheme } = useVoicesTheme();
  const themeLabel = `Switch to ${theme === 'dark' ? 'white' : 'black'} theme`;
  const [activeDialog, setActiveDialog] = useState(null);
  const headerRef = useRef(null);
  const returnFocus = useRef(null);
  const searchInput = useRef(null);
  const menuStart = useRef(null);
  const id = useId();
  const records = useMemo(() => getDirectory(blogs), [blogs]);
  const closeDialog = useCallback(() => setActiveDialog(null), []);
  const home = location.pathname === "/";
  const interiorScene = hasVoicesInteriorScene(location.pathname);
  const interior = !home && isVoicesRoute(location.pathname);

  useEffect(() => { closeDialog(); }, [location, closeDialog]);

  useEffect(() => {
    const search = event => {
      returnFocus.current = event.detail?.trigger || document.activeElement;
      setActiveDialog('search');
    };
    window.addEventListener('voices:search', search);
    return () => window.removeEventListener('voices:search', search);
  }, []);

  const openDialog = (dialog, event) => {
    if (!activeDialog) returnFocus.current = event.currentTarget;
    setActiveDialog(dialog);
  };

  return (
    <>
      <div className={`voices-header-slot${home || interiorScene ? " voices-header-slot--home" : ""}`}>
        <header ref={headerRef} className={`voices-header voices-header--${theme}${home ? ' voices-header--home' : interior ? ' voices-header--interior' : ''}`}>
          <div className="voices-header-inner">
            <Link to="/" className="voices-brand" aria-label="Antrazal home">
              <img src={`/assets/voices/antrazal-${theme === 'dark' ? 'white' : 'blue'}.webp`} alt="Antrazal" width="131" height="30" />
            </Link>
            <nav className="voices-desktop-nav" aria-label="Main navigation" onKeyDown={moveLinkFocus}>
              <NavLink to="/services">Services</NavLink>
              <NavLink to="/work">Work</NavLink>
              <NavLink to="/who-we-are">Company</NavLink>
            </nav>
            <div className="voices-header-actions">
              <Link to="/contact" className="voices-talk">Let's talk<FiArrowUpRight aria-hidden="true" /></Link>
              <button type="button" className="voices-icon-button voices-menu-trigger" aria-label="Open site navigation" title="Open site navigation" aria-haspopup="dialog" aria-expanded={activeDialog === "menu"} aria-controls={activeDialog === "menu" ? `${id}-menu` : undefined} onClick={(event) => openDialog("menu", event)}>
                <HiOutlineMenuAlt4 aria-hidden="true" />
              </button>
              <button type="button" className="voices-icon-button voices-theme-toggle" aria-label={themeLabel} title={themeLabel} aria-pressed={theme === 'light'} onClick={toggleTheme}>
                {theme === 'dark' ? <FiSun aria-hidden="true" /> : <FiMoon aria-hidden="true" />}
                <span className="voices-theme-tooltip" aria-hidden="true">{themeLabel}</span>
              </button>
            </div>
          </div>
        </header>
      </div>
      {activeDialog === "menu" && (
        <ShellDialog key="menu" id={`${id}-menu`} title="Site navigation" kind="menu" onClose={closeDialog} returnFocus={returnFocus} initialFocus={menuStart}>
          <nav className="voices-menu-grid" aria-label="Site directory" onKeyDown={moveLinkFocus}>
            <div className="voices-menu-primary">
              <Link ref={menuStart} to="/" onClick={closeDialog}>Home</Link>
              <Link to="/services" onClick={closeDialog}>Services</Link>
              <Link to="/work" onClick={closeDialog}>Our work</Link>
              <Link to="/who-we-are" onClick={closeDialog}>Company</Link>
              <Link to="/contact" onClick={closeDialog}>Let's talk<FiArrowUpRight aria-hidden="true" /></Link>
            </div>
            <div className="voices-menu-secondary">
              <h3>More from Antrazal</h3>
              {FEATURED_SERVICES.map(([path, label]) => <Link key={path} to={path} onClick={closeDialog}>{label}</Link>)}
              {PAGES.filter((page) => ["/case-studies", "/careers", "/events", "/products/edi-converter", "/site-map", "/privacy-policy"].includes(page.pathname)).map((page) => <Link key={page.pathname} to={page.pathname} onClick={closeDialog}>{page.title}</Link>)}
            </div>
          </nav>
          <div className="voices-menu-bottom">
            <button type="button" className="voices-text-button" onClick={(event) => openDialog("search", event)}><FiSearch aria-hidden="true" />Search the site</button>
            <a href="mailto:info@antrazal.com">info@antrazal.com</a>
            <div className="voices-menu-social">
              <a href="https://in.linkedin.com/company/antrazal-solutions-private-limited" className="voices-icon-button" aria-label="Antrazal on LinkedIn" title="Antrazal on LinkedIn"><FiLinkedin aria-hidden="true" /></a>
              <a href="https://www.instagram.com/antrazal?igsh=MjZrY2l3NWw5b3g1" className="voices-icon-button" aria-label="Antrazal on Instagram" title="Antrazal on Instagram"><FiInstagram aria-hidden="true" /></a>
            </div>
          </div>
          <LanguageLink pathname={records.some((page) => page.pathname === location.pathname) ? location.pathname : "/"} />
        </ShellDialog>
      )}
      {activeDialog === "search" && (
        <ShellDialog key="search" id={`${id}-search`} title="Search Antrazal" onClose={closeDialog} returnFocus={returnFocus} initialFocus={searchInput}>
          <SiteSearch records={records} inputRef={searchInput} onClose={closeDialog} />
        </ShellDialog>
      )}
    </>
  );
}
